import type { ActivationTaskId } from '../../../redux/ownerActivationSlice';

/**
 * Static copy for the Home activation tracker task rows (US5, T072).
 *
 * Labels are the exact strings from the UI/UX contract §5. Descriptions are a
 * one-line value explanation; CTA labels drive the incomplete-state action.
 * Dependency notes are shown only when a task's prerequisite is unmet.
 */

export interface ActivationTaskCopy {
  label: string;
  description: string;
  ctaLabel: string;
  /** Shown when the task is blocked by an unmet dependency. */
  dependencyNote?: string;
}

export const ACTIVATION_TASK_CONFIG: Record<
  ActivationTaskId,
  ActivationTaskCopy
> = {
  add_product: {
    label: 'Add your first product',
    description: 'Stock a real product so you can start selling.',
    ctaLabel: 'Add product',
  },
  first_sale: {
    label: 'Complete your first sale',
    description: 'Ring up a real sale to see the full checkout loop.',
    ctaLabel: 'Start a sale',
    dependencyNote: 'Add a product first',
  },
  explore_backoffice: {
    label: 'Explore Back Office',
    description: 'Manage staff, reports, and settings.',
    ctaLabel: 'Open Back Office',
  },
  add_staff: {
    label: 'Add a staff member',
    description: 'Invite your team to help run the shop.',
    ctaLabel: 'Add staff',
  },
  visit_test_location: {
    label: 'Visit your test location',
    description: 'A safe place to practice without touching real data.',
    ctaLabel: 'Switch location',
  },
};

/**
 * Stable render/priority order of tracker task rows.
 *
 * `add_staff` is intentionally omitted — see the note on getRequiredTaskIds
 * in redux/ownerActivationSlice.ts. The copy above stays for any residual
 * references (analytics, bootstrap) but the row is not rendered.
 */
export const ACTIVATION_TASK_ORDER: ActivationTaskId[] = [
  'add_product',
  'first_sale',
  'explore_backoffice',
  'visit_test_location',
];
